"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useState } from "react";
import { Ban, BookOpen, Check, ChevronDown, CircleAlert, CircleCheck, Copy, FileText, PencilLine, Settings2, Trash2, X } from "lucide-react";
import FitHex from "@/features/collections/detail/FitHex";
import { chainName } from "@/features/home-v2/tiles";
import { ActionButton } from "@/features/home-v2/form/Action";
import { Dialog } from "@/features/home-v2/ui";
import type { TransactionRecord } from "@/lib/transaction-flow";
import type { AuctionItem } from "../../types";
import { formatDateTime, STATUS_LABEL } from "./format";
import { useAuctionOwner } from "./useAuctionOwner";
import { withPercent } from "@/lib/percent";
export type Fold = "open" | "closed";

/**
 * The panel every lower section wears — the comments' own frame: a hairline
 * box, a lit head bar with a gilt icon, an optional count. With `fold` the
 * head is a button that opens and closes the body, a chevron saying so.
 */
export function Panel(t0) {
  const $ = _c(18);
  const {
    id,
    icon,
    title,
    count,
    children,
    className: t1,
    fold
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const [open, setOpen] = useState(fold !== "closed");
  const body = `${id}-body`;
  const t2 = `ap-panel ${className}`;
  const t3 = fold ? open ? "open" : "closed" : undefined;
  let t4;
  if ($[0] !== body || $[1] !== count || $[2] !== fold || $[3] !== icon || $[4] !== id || $[5] !== open || $[6] !== title) {
    t4 = fold ? <h2 className="ap-panel-head ap-panel-head--fold"><button type="button" id={id} className="v2-focus" aria-expanded={open} aria-controls={body} onClick={() => setOpen(_temp)}>{icon}<span>{title}</span>{count ? <span className="ap-panel-count">{count}</span> : null}<ChevronDown className="ap-chev" aria-hidden={true} /></button></h2> : <header className="ap-panel-head">{icon}<h2 id={id}>{title}</h2>{count ? <span className="ap-panel-count">{count}</span> : null}</header>;
    $[0] = body;
    $[1] = count;
    $[2] = fold;
    $[3] = icon;
    $[4] = id;
    $[5] = open;
    $[6] = title;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = fold ? !open : undefined;
  let t6;
  if ($[8] !== body || $[9] !== children || $[10] !== t5) {
    t6 = <div className="ap-panel-body" id={body} hidden={t5}>{children}</div>;
    $[8] = body;
    $[9] = children;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== id || $[13] !== t2 || $[14] !== t3 || $[15] !== t4 || $[16] !== t6) {
    t7 = <section className={t2} aria-labelledby={id} data-fold={t3}>{t4}{t6}</section>;
    $[12] = id;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
}

/**
 * The work's record — every fact the legacy details listed, translated, now
 * read in three groups (the piece, the timing, the rules) as small cells
 * instead of one long list; the contract on its own line, whole where it fits.
 */
function _temp(v) {
  return !v;
}
export function AuctionDetails(t0) {
  const $ = _c(19);
  const {
    auction,
    bidsCount,
    lang,
    fold
  } = t0;
  const isFa = lang === "fa";
  let t1;
  if ($[0] !== isFa) {
    t1 = (fa, en) => isFa ? fa : en;
    $[0] = isFa;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  const nf = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US");
  const md = auction.model_data;
  const [copied, setCopied] = useState(false);
  const standard = md?.standard === 721 ? "ERC-721" : md?.standard === 1155 ? "ERC-1155" : md?.standard ? `#${md.standard}` : null;
  const status = STATUS_LABEL[auction.status];
  let t2;
  if ($[2] !== md) {
    t2 = async () => {
      if (!md?.contract_address) {
        return;
      }
      try {
        await navigator.clipboard.writeText(md.contract_address);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch {}
    };
    $[2] = md;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const copy = t2;
  const groups = [{
    k: t("\u0627\u062B\u0631", "The piece"),
    cells: [...(standard ? [[t("\u0627\u0633\u062A\u0627\u0646\u062F\u0627\u0631\u062F", "Standard"), <bdi key="s">{standard}</bdi>] as Cell] : []), ...(md?.token_id != null ? [[t("\u0634\u0646\u0627\u0633\u0647\u200C\u06CC \u062A\u0648\u06A9\u0646", "Token ID"), <bdi key="i">#{md.token_id}</bdi>] as Cell] : []), [t("\u0634\u0628\u06A9\u0647", "Chain"), chainName(md?.network_name || auction.network?.name, isFa) || "\u2014"], ...(md?.quantity != null ? [[t("\u062A\u0639\u062F\u0627\u062F", "Quantity"), nf.format(md.quantity)] as Cell] : []), ...(md?.royalty != null ? [[t("\u0633\u0647\u0645 \u0633\u0627\u0632\u0646\u062F\u0647 \u0627\u0632 \u0641\u0631\u0648\u0634 \u0628\u0639\u062F\u06CC", "Creator's resale share"), withPercent(nf.format(md.royalty), isFa)] as Cell] : [])]
  }, {
    k: t("\u0632\u0645\u0627\u0646\u200C\u0628\u0646\u062F\u06CC", "Timing"),
    cells: [[t("\u0634\u0631\u0648\u0639", "Opens"), formatDateTime(auction.start_time, isFa)], [t("\u067E\u0627\u06CC\u0627\u0646", "Closes"), formatDateTime(auction.expire_time, isFa)]]
  }, {
    k: t("\u0642\u0648\u0627\u0639\u062F \u0645\u0632\u0627\u06CC\u062F\u0647", "The rules"),
    cells: [[t("\u067E\u06CC\u0634\u0646\u0647\u0627\u062F \u0647\u0631 \u0646\u0641\u0631", "Bids per person"), nf.format(auction.max_bids_per_user)], [t("\u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u0647\u0627 \u062A\u0627 \u062D\u0627\u0644\u0627", "Bids so far"), nf.format(bidsCount)], [t("\u0648\u0636\u0639\u06CC\u062A", "Status"), status ? isFa ? status[0] : status[1] : auction.status]]
  }];
  const T0 = Panel;
  const t3 = "as-details-h";
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <FileText aria-hidden={true} />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t) {
    t5 = t("\u0634\u0646\u0627\u0633\u0646\u0627\u0645\u0647\u200C\u06CC \u0627\u062B\u0631", "About this piece");
    $[5] = t;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== copied || $[8] !== copy || $[9] !== md || $[10] !== t) {
    t6 = md?.contract_address ? <div className="ap-contract"><span>{t("\u0642\u0631\u0627\u0631\u062F\u0627\u062F", "Contract")}</span><button type="button" className="as-copy v2-focus" onClick={copy} title={md.contract_address} aria-label={`${t("\u06A9\u067E\u06CC \u0622\u062F\u0631\u0633 \u0642\u0631\u0627\u0631\u062F\u0627\u062F", "Copy the contract address")}: ${md.contract_address}`}><FitHex value={md.contract_address} className="as-hex" />{copied ? <Check aria-hidden={true} /> : <Copy aria-hidden={true} />}</button></div> : null;
    $[7] = copied;
    $[8] = copy;
    $[9] = md;
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = groups.map(_temp3);
  let t8;
  if ($[12] !== T0 || $[13] !== fold || $[14] !== t4 || $[15] !== t5 || $[16] !== t6 || $[17] !== t7) {
    t8 = <T0 id={t3} icon={t4} title={t5} fold={fold}>{t6}{t7}</T0>;
    $[12] = T0;
    $[13] = fold;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}

/** The work's own note — folded after a few lines, opened in place. */
function _temp3(g) {
  return <div key={g.k} className="ap-group"><span className="ap-group-k">{g.k}</span><dl className="ap-cells">{g.cells.map(_temp2)}</dl></div>;
}
function _temp2(t0) {
  const [k, v] = t0;
  return <div key={k}><dt>{k}</dt><dd>{v}</dd></div>;
}
export function AuctionStory(t0) {
  const $ = _c(15);
  const {
    text,
    lang,
    fold
  } = t0;
  const isFa = lang === "fa";
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] !== text) {
    t1 = text.length > 320 || text.split("\n").length > 5;
    $[0] = text;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const long = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <BookOpen aria-hidden={true} />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = isFa ? "\u062F\u0631\u0628\u0627\u0631\u0647\u200C\u06CC \u0627\u062B\u0631" : "About the work";
  const t4 = open || !long || undefined;
  let t5;
  if ($[3] !== t4 || $[4] !== text) {
    t5 = <p className="as-story" dir="auto" data-open={t4}>{text}</p>;
    $[3] = t4;
    $[4] = text;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== isFa || $[7] !== long || $[8] !== open) {
    t6 = long ? <button type="button" className="as-more v2-focus" onClick={() => setOpen(_temp4)} aria-expanded={open}>{open ? isFa ? "\u06A9\u0645\u062A\u0631" : "Less" : isFa ? "\u0627\u062F\u0627\u0645\u0647" : "Read more"}</button> : null;
    $[6] = isFa;
    $[7] = long;
    $[8] = open;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== fold || $[11] !== t3 || $[12] !== t5 || $[13] !== t6) {
    t7 = <Panel id="as-story-h" icon={t2} title={t3} fold={fold}>{t5}{t6}</Panel>;
    $[10] = fold;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}

/** What this browser did here — kept after a reload, each dismissable. */
function _temp4(v) {
  return !v;
}
export function AuctionResults(t0) {
  const $ = _c(10);
  const {
    records,
    onDismiss,
    lang
  } = t0;
  const isFa = lang === "fa";
  if (records.length === 0) {
    return null;
  }
  const t1 = isFa ? "\u0646\u062A\u06CC\u062C\u0647\u200C\u06CC \u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u0647\u0627\u06CC \u0634\u0645\u0627" : "Your bid results";
  let t2;
  if ($[0] !== isFa || $[1] !== onDismiss || $[2] !== records) {
    let t3;
    if ($[4] !== isFa || $[5] !== onDismiss) {
      t3 = r => <div key={r.requestId} className="as-result" data-outcome={r.outcome} role="status">{r.outcome === "success" ? <CircleCheck aria-hidden={true} /> : <CircleAlert aria-hidden={true} />}<div><b>{r.title}</b>{r.description ? <p>{r.description}</p> : null}</div><button type="button" className="as-result-x v2-focus" onClick={() => onDismiss(r.requestId)} aria-label={isFa ? "\u0628\u0633\u062A\u0646 \u0627\u06CC\u0646 \u0646\u062A\u06CC\u062C\u0647" : "Dismiss this result"}><X aria-hidden={true} /></button></div>;
      $[4] = isFa;
      $[5] = onDismiss;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t2 = records.map(t3);
    $[0] = isFa;
    $[1] = onDismiss;
    $[2] = records;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = <section className="as-results" aria-label={t1}>{t2}</section>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}

/** The owner's tools — AuctionOwnerActions' rules: edit and cancel a published draft, delete a published auction; each confirmed first. */
export function AuctionOwnerPanel(t0) {
  const $ = _c(70);
  const {
    auction,
    canEdit,
    canCancel,
    canDelete,
    lang
  } = t0;
  const isFa = lang === "fa";
  let t1;
  if ($[0] !== isFa) {
    t1 = (fa, en) => isFa ? fa : en;
    $[0] = isFa;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  let t2;
  if ($[2] !== auction.id || $[3] !== lang) {
    t2 = {
      auctionId: auction.id,
      lang
    };
    $[2] = auction.id;
    $[3] = lang;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const o = useAuctionOwner(t2);
  if (!canEdit && !canCancel && !canDelete) {
    return null;
  }
  const isDelete = o.pendingAction === "delete";
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Settings2 aria-hidden={true} />;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t) {
    t4 = t("\u0645\u062F\u06CC\u0631\u06CC\u062A \u0645\u0632\u0627\u06CC\u062F\u0647", "Manage this auction");
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== auction.slug || $[9] !== canEdit || $[10] !== lang || $[11] !== t) {
    t5 = canEdit ? <Link href={`/${lang}/auctions/${auction.slug}/edit`} className="v2-btn v2-btn--ghost v2-focus"><PencilLine aria-hidden={true} />{t("\u0648\u06CC\u0631\u0627\u06CC\u0634", "Edit")}</Link> : null;
    $[8] = auction.slug;
    $[9] = canEdit;
    $[10] = lang;
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== canCancel || $[14] !== o || $[15] !== t) {
    t6 = canCancel ? <button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={() => o.ask("cancel")}><Ban aria-hidden={true} />{t("\u0644\u063A\u0648 \u0645\u0632\u0627\u06CC\u062F\u0647", "Cancel auction")}</button> : null;
    $[13] = canCancel;
    $[14] = o;
    $[15] = t;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== canDelete || $[18] !== o || $[19] !== t) {
    t7 = canDelete ? <button type="button" className="v2-btn v2-btn--ghost as-danger-ghost v2-focus" onClick={() => o.ask("delete")}><Trash2 aria-hidden={true} />{t("\u062D\u0630\u0641 \u0645\u0632\u0627\u06CC\u062F\u0647", "Delete auction")}</button> : null;
    $[17] = canDelete;
    $[18] = o;
    $[19] = t;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t5 || $[22] !== t6 || $[23] !== t7) {
    t8 = <div className="as-owner-tools">{t5}{t6}{t7}</div>;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const t9 = Boolean(o.pendingAction);
  const t10 = o.close;
  let t11;
  if ($[25] !== isDelete || $[26] !== t) {
    t11 = isDelete ? t("\u062D\u0630\u0641 \u0645\u0632\u0627\u06CC\u062F\u0647", "Delete the auction") : t("\u0644\u063A\u0648 \u0645\u0632\u0627\u06CC\u062F\u0647", "Cancel the auction");
    $[25] = isDelete;
    $[26] = t;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t) {
    t12 = t("\u0628\u0633\u062A\u0646", "Close");
    $[28] = t;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const t13 = o.isSubmitting;
  const t14 = o.close;
  const t15 = o.isSubmitting;
  let t16;
  if ($[30] !== t) {
    t16 = t("\u0627\u0646\u0635\u0631\u0627\u0641", "Cancel");
    $[30] = t;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== o.close || $[33] !== o.isSubmitting || $[34] !== t16) {
    t17 = <button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={t14} disabled={t15}>{t16}</button>;
    $[32] = o.close;
    $[33] = o.isSubmitting;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  const t18 = isDelete ? "as-danger" : undefined;
  let t19;
  if ($[36] !== o) {
    t19 = () => void o.confirm();
    $[36] = o;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== isDelete || $[39] !== t) {
    t20 = isDelete ? t("\u062F\u0631 \u062D\u0627\u0644 \u062D\u0630\u0641\u2026", "Deleting\u2026") : t("\u062F\u0631 \u062D\u0627\u0644 \u0644\u063A\u0648\u2026", "Canceling\u2026");
    $[38] = isDelete;
    $[39] = t;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== isDelete || $[42] !== t) {
    t21 = isDelete ? t("\u062D\u0630\u0641 \u0645\u0632\u0627\u06CC\u062F\u0647", "Delete auction") : t("\u0644\u063A\u0648 \u0645\u0632\u0627\u06CC\u062F\u0647", "Cancel auction");
    $[41] = isDelete;
    $[42] = t;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== o.isSubmitting || $[45] !== t18 || $[46] !== t19 || $[47] !== t20 || $[48] !== t21) {
    t22 = <ActionButton className={t18} onClick={t19} busy={o.isSubmitting} busyLabel={t20}>{t21}</ActionButton>;
    $[44] = o.isSubmitting;
    $[45] = t18;
    $[46] = t19;
    $[47] = t20;
    $[48] = t21;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t17 || $[51] !== t22) {
    t23 = <div className="as-dlg-foot">{t17}{t22}</div>;
    $[50] = t17;
    $[51] = t22;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== isDelete || $[54] !== t) {
    t24 = isDelete ? t("\u0645\u0632\u0627\u06CC\u062F\u0647 \u062D\u0630\u0641 \u0645\u06CC\u200C\u0634\u0648\u062F \u0648 \u0642\u0627\u0628\u0644 \u0628\u0631\u06AF\u0634\u062A \u0646\u06CC\u0633\u062A.", "The auction is deleted. This can't be undone.") : t("\u0645\u0632\u0627\u06CC\u062F\u0647 \u0644\u063A\u0648 \u0645\u06CC\u200C\u0634\u0648\u062F \u0648 \u062F\u06CC\u06AF\u0631 \u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u06CC \u0646\u0645\u06CC\u200C\u067E\u0630\u06CC\u0631\u062F.", "The auction is canceled and takes no more bids.");
    $[53] = isDelete;
    $[54] = t;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== t24) {
    t25 = <p className="as-dlg-lead">{t24}</p>;
    $[56] = t24;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== o.close || $[59] !== o.isSubmitting || $[60] !== t11 || $[61] !== t12 || $[62] !== t23 || $[63] !== t25 || $[64] !== t9) {
    t26 = <Dialog open={t9} onClose={t10} title={t11} closeLabel={t12} className="as-dialog" busy={t13} footer={t23}>{t25}</Dialog>;
    $[58] = o.close;
    $[59] = o.isSubmitting;
    $[60] = t11;
    $[61] = t12;
    $[62] = t23;
    $[63] = t25;
    $[64] = t9;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  let t27;
  if ($[66] !== t26 || $[67] !== t4 || $[68] !== t8) {
    t27 = <Panel id="as-owner-h" icon={t3} title={t4}>{t8}{t26}</Panel>;
    $[66] = t26;
    $[67] = t4;
    $[68] = t8;
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  return t27;
}